import React, { useEffect } from 'react';
import { Check, ExternalLink, Users, Target, ShieldCheck } from 'lucide-react';
import { Button } from './ui/button';
import { useNavigate } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import Reveal from './Reveal';
import { useLang } from '../context/LanguageContext';

const PLATFORMS = [
  { id: 'strongfit-studio', name: 'StrongFit Studio', image: '/screenshots/strongfit-app.jpg', liveUrl: 'https://app.strongfit.ch' },
  { id: 'reinmove', name: 'ReinMove', image: '/screenshots/reinmove.jpg', liveUrl: 'https://www.reinmove.ch' },
  { id: 'trucksonroad', name: 'TrucksOnRoad', image: '/screenshots/trucksonroad.jpg', liveUrl: 'https://www.trucksonroad.ch' },
  { id: 'bellastrada', name: 'Bella Strada', image: '/screenshots/bellastrada.jpg', liveUrl: 'https://www.bellastrada.ch' },
  { id: 'zo-werbeflaechen', name: 'ZO Werbeflächen', image: '/screenshots/zo-werbeflaechen.jpg', liveUrl: 'https://www.zo-werbeflaechen.ch' },
  { id: 'ch-buchhaltung', name: 'CH-Buchhaltung', image: '/screenshots/ch-buchhaltung.jpg', liveUrl: 'https://ch-buchhaltung.ch' },
];

const PlatformCard = ({ platform, index }) => {
  const { t } = useLang();
  const reversed = index % 2 === 1;
  const info = t(`platforms.items.${platform.id}`) || {};

  return (
    <div
      className={`grid md:grid-cols-2 gap-10 items-center ${reversed ? 'md:[direction:rtl]' : ''}`}
      data-testid={`platform-card-${platform.id}`}
    >
      <div className="md:[direction:ltr]">
        <div className="rounded-3xl overflow-hidden border border-[#D2D2D7] bg-white shadow-sm">
          <img
            src={platform.image}
            alt={platform.name}
            className="w-full h-auto object-cover"
            loading="lazy"
          />
        </div>
      </div>

      <div className="md:[direction:ltr]">
        <p className="text-xs font-semibold tracking-widest uppercase text-[#86868B] mb-2">
          {info.category}
        </p>
        <h2 className="text-2xl md:text-3xl font-semibold text-[#1D1D1F] mb-4">{platform.name}</h2>

        <div className="flex items-start space-x-3 mb-4">
          <Target className="w-5 h-5 text-[#0071E3] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <p className="text-[#424245] text-sm leading-relaxed">{info.purpose}</p>
        </div>

        <div className="flex items-start space-x-3 mb-5">
          <Users className="w-5 h-5 text-[#0071E3] mt-0.5 flex-shrink-0" strokeWidth={1.5} />
          <p className="text-[#424245] text-sm leading-relaxed">
            <span className="font-semibold text-[#1D1D1F]">{t('platforms.audiencePrefix')} </span>
            {info.audience}
          </p>
        </div>

        <ul className="space-y-2 mb-6">
          {(info.functions || []).map((fn, i) => (
            <li key={i} className="flex items-start space-x-2 text-sm text-[#424245]">
              <Check className="w-4 h-4 text-[#0071E3] mt-0.5 flex-shrink-0" strokeWidth={2} />
              <span>{fn}</span>
            </li>
          ))}
        </ul>

        <div className="flex items-center space-x-2 text-xs text-[#86868B] mb-5">
          <ShieldCheck className="w-4 h-4 flex-shrink-0" strokeWidth={1.5} />
          <span>{t('platforms.trust')}</span>
        </div>

        {platform.liveUrl && (
          <a href={platform.liveUrl} target="_blank" rel="noopener noreferrer">
            <Button
              variant="outline"
              className="rounded-full border-[#0071E3] text-[#0071E3] hover:bg-[#0071E3] hover:text-white text-sm h-10 px-5"
              data-testid={`platform-live-link-${platform.id}`}
            >
              {t('platforms.toPlatform')}
              <ExternalLink className="w-4 h-4 ml-2" strokeWidth={1.5} />
            </Button>
          </a>
        )}
      </div>
    </div>
  );
};

export default function Platforms() {
  const navigate = useNavigate();
  const { t } = useLang();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Header />
      <main className="bg-white" data-testid="platforms-page">
        <section className="pt-36 pb-16 px-6 bg-[#F5F5F7]">
          <div className="max-w-[980px] mx-auto text-center">
            <p className="text-sm font-semibold text-[#0071E3] mb-3 tracking-wide">
              InvictaX Group GmbH
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-semibold text-[#1D1D1F] tracking-tight mb-5">
              {t('platforms.title')}
            </h1>
            <p className="text-base md:text-lg text-[#86868B] max-w-2xl mx-auto leading-relaxed">
              {t('platforms.intro')}
            </p>
          </div>
        </section>

        <section className="py-20 px-6">
          <div className="max-w-[980px] mx-auto space-y-24">
            {PLATFORMS.map((platform, index) => (
              <Reveal key={platform.id}>
                <PlatformCard platform={platform} index={index} />
              </Reveal>
            ))}
          </div>
        </section>

        <section className="py-20 px-6 bg-[#F5F5F7]">
          <div className="max-w-[980px] mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-semibold text-[#1D1D1F] mb-4">
              {t('platforms.ctaTitle')}
            </h2>
            <p className="text-[#86868B] mb-8 max-w-xl mx-auto">
              {t('platforms.ctaText')}
            </p>
            <Button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }, 300);
              }}
              className="bg-[#0071E3] hover:bg-[#0077ED] text-white rounded-full h-12 px-8"
              data-testid="platforms-contact-cta"
            >
              {t('platforms.ctaBtn')}
            </Button>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
